"use client";

import { useState } from "react";
import { CopyButton } from "@/components/CopyButton";
import { site } from "@/lib/site";
import { cn } from "@/lib/utils";

const RAW_BASE = "https://raw.githubusercontent.com/Raktim94/nodedr-pos/master/scripts";

const OS_TABS = [
  {
    id: "deb",
    label: "Ubuntu / Debian",
    command: `curl -fsSL ${site.installLinuxUrl} | sudo bash`,
    note: "Package for Ubuntu 24.04 and Debian 12 or newer, amd64 and arm64, with systemd. It sets up Docker if needed, runs NodeDR POS in Docker and starts it. Read the script first, or download the .deb below and run sudo apt install ./file.deb.",
    downloads: [["Download .deb (amd64)", site.debAmd64Url], ["Download .deb (arm64)", site.debArm64Url]],
  },
  {
    id: "mac",
    label: "macOS",
    command: `curl -fsSL ${RAW_BASE}/quickstart.sh | bash`,
    note: "Paste into Terminal. Installs Docker Desktop and git through Homebrew if needed, then installs NodeDR POS.",
  },
  {
    id: "linux",
    label: "Linux",
    command: `curl -fsSL ${RAW_BASE}/quickstart.sh | bash`,
    note: "Paste into a terminal. Installs Docker Engine and git if needed, then installs NodeDR POS.",
  },
  {
    id: "windows",
    label: "Windows",
    command: `irm ${RAW_BASE}/quickstart.ps1 | iex`,
    note: "Paste into PowerShell. Sets up WSL2 and Docker if needed, then installs NodeDR POS. A restart may be required the first time WSL2 is enabled.",
  },
] as const;

export function InstallCommand({ className }: { className?: string }) {
  const [activeId, setActiveId] = useState<(typeof OS_TABS)[number]["id"]>("deb");
  const active = OS_TABS.find((os) => os.id === activeId)!;

  return (
    <div className={className}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        Copy one command to install NodeDR POS
      </p>
      <div role="tablist" aria-label="Choose your computer" className="mb-2 inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1 flex-wrap max-w-full">
        {OS_TABS.map((os) => (
          <button
            key={os.id}
            type="button"
            role="tab"
            aria-selected={os.id === activeId}
            onClick={() => setActiveId(os.id)}
            className={cn(
              "rounded-md px-3 py-1 text-xs font-semibold transition",
              os.id === activeId ? "bg-brand-700 text-white" : "text-slate-500 hover:text-slate-900"
            )}
          >
            {os.label}
          </button>
        ))}
      </div>

      <div className="cmd-block">
        <span className="prompt">$</span>
        <span className="min-w-0 flex-1 select-all break-all text-xs leading-relaxed">{active.command}</span>
        <CopyButton text={active.command} className="h-9 w-9" />
      </div>
      <p className="mt-2 text-xs text-slate-500">{active.note}</p>
      {"downloads" in active && (
        <p className="mt-3 flex flex-wrap gap-2">
          {active.downloads.map(([label, href]) => (
            <a key={href} href={href} className="inline-flex items-center rounded-lg bg-brand-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700">
              {label}
            </a>
          ))}
          <a href="/install-linux.sh" className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-800 transition hover:bg-slate-50">Read the script</a>
          <span className="inline-flex items-center text-xs text-slate-700">
            v{site.debVersion} · <a className="ml-1 underline underline-offset-2" href={`https://github.com/Raktim94/nodedr-pos/releases/tag/v${site.debVersion}`}>release notes</a>
          </span>
        </p>
      )}
    </div>
  );
}
